import { post } from '@/api/client';
import { forgetDestination } from '@/auth/destination';
import { type SessionUser, useAuth } from '@/auth/store';
import { T } from '@/i18n';
/**
 * /se/:code: «Se selv» in a cold email (plan 0030, owner 2026-10-05). The signed code names the company;
 * the button opens a demo named after it, made on the first press, and Vaktplan opens with Hugin
 * building next week while the visitor watches. Opening the page creates nothing: a mail scanner or an
 * inbox preview that follows the link sees this page only. The server checks the code; the name shown
 * here is read from it for display only.
 */
import { askOnOpen } from '@/poc-chat/Panel';
import { commitGeneration } from '@/shell/context';
import { Button } from '@/ui';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { KontoBox, failureCode, failureText, hint } from './shared';

interface Opened {
  new_demo?: boolean;
  token: string | null;
  role: string | null;
  user: SessionUser;
}

/** The company name in the code, for the page only; null when it cannot be read. */
export function nameIn(code: string): string | null {
  try {
    const body = code.split('.')[0] ?? '';
    const json = atob(body.replace(/-/g, '+').replace(/_/g, '/'));
    const name = JSON.parse(new TextDecoder().decode(Uint8Array.from(json, (c) => c.charCodeAt(0)))).name;
    return typeof name === 'string' && name.length <= 120 ? name : null;
  } catch {
    return null;
  }
}

export const FIRST_QUESTION = 'Lag vaktplan for neste uke';

export default function SeSelv() {
  const { code = '' } = useParams();
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; account: boolean } | null>(null);
  const started = useRef(false); // a second press before React renders
  const name = nameIn(code);

  function start() {
    if (started.current) return;
    started.current = true;
    setBusy(true);
    post<Opened>('/account/outreach-demo', { code })
      .then((r) => {
        if (!r.token) {
          started.current = false;
          return setErr({ text: T('Demoen ble laget, men kunne ikke åpnes. Prøv igjen.'), account: false });
        }
        setSession(r.token, { ...r.user, role: r.role });
        commitGeneration(r.user.id, null, null); // the token names the demo; nothing from before is readable
        forgetDestination();
        // a new demo: Hugin builds next week in front of them; one seen before opens as it was left
        if (r.new_demo) askOnOpen(T(FIRST_QUESTION));
        nav('/vaktplan', { replace: true });
      })
      .catch((ex) => {
        started.current = false;
        setErr({ text: failureText(ex), account: failureCode(ex) === 'has_account' });
      })
      .finally(() => setBusy(false));
  }

  return (
    <KontoBox title={name ? `${name} ${T('i Timefly')}` : T('Din demo i Timefly')}>
      <div className="opt" style={{ display: 'grid', gap: 10 }}>
        {err ? (
          <>
            <div className="al dgr" style={{ margin: 0 }}>
              {err.text}
            </div>
            {err.account ? (
              <Link to="/login" style={hint}>
                {T('Logg inn')}
              </Link>
            ) : (
              <Link to="/registrer?next=demo" style={hint}>
                {T('Be om en demo')}
              </Link>
            )}
          </>
        ) : (
          <>
            <p style={hint}>
              {T('Trykk, så lager Hugin, AI-en i Timefly, neste ukes vaktplan for dere mens du ser på.')}
            </p>
            <Button tone="pri" type="button" onClick={start} disabled={busy}>
              {busy ? T('Setter opp demoen …') : T('Se Hugin lage uka')}
            </Button>
            <p style={hint}>{T('Ingen registrering. Demoen slettes etter 30 dager.')}</p>
          </>
        )}
      </div>
    </KontoBox>
  );
}
